// Libraries
import { Box, Flex, Skeleton, Text } from '@chakra-ui/react';
// Components
import { RoundedArrowIcon } from '@atoms/index';
// Types
import { IconTextProps, Direction } from './IconText.type';

/**
 * IconText component that allows to show a text preceded by an arrow icon.
 *
 * @component
 * @param {IconTextProps} props - The component props.
 * @param {string} props.text - String or number with the text to be displayed.
 * @param {DirectionKeys} props.direction - Use 'up' to show upward arrow and 'down' to show downward arrow.
 * @param {boolean} props.isLoading - Flag to show loading state.
 * @param {boolean} props.skeletonLength - Numeric value that determines the length of the loading skeleton.
 * @param {TextProps} props.rest - Allows to use any property of the base Text component.
 * @returns {ReactElement} - The AvatarButton component.
 */
export const IconText = ({ direction = Direction.up, children, isLoading = false, ...rest }: IconTextProps) => {
  const transform = direction === Direction.down ? `rotate(90deg)` : `rotate(270deg)`;

  return (
    <Flex alignItems='center'>
      <Skeleton isLoaded={!isLoading} borderRadius='0' display='flex' fitContent alignItems='center'>
        <Box as='span' display='flex'>
          <RoundedArrowIcon
            w='10px'
            h='10px'
            transform={transform}
            transition='all'
            transitionDuration='1s'
            color='#8484A0'
          />
        </Box>
        <Box as='span' ml='2px' whiteSpace='nowrap'>
          <Text variant='tooltip' display='flex' alignItems='center' lineHeight='16px' whiteSpace='nowrap' {...rest}>
            {children}
          </Text>
        </Box>
      </Skeleton>
    </Flex>
  );
};
